/**
 * Framework-agnostic IANA timezone helpers.
 *
 * Mirror of `takshify-shared/src/timezone.ts` (canonical, unit-tested there).
 * takshify-shared is not wired as a package dependency yet, so this follows the
 * same copy convention as `inlineMarkdown` / `pathLessonParser`. Keep in sync.
 *
 * Design notes:
 * - Instants are always the source of truth (TIMESTAMPTZ / ISO strings). These
 *   helpers only decide how an instant is *displayed* and which calendar day it
 *   falls in for a given zone.
 * - Day bucketing uses Intl, which is DST-correct and needs no date library.
 * - Wall-clock -> instant conversion (needed to author times in an institute's
 *   zone) is deliberately NOT here; it needs an offset probe and belongs with
 *   the school authoring change.
 */

export const PLATFORM_DEFAULT_TIMEZONE = 'Asia/Kolkata';

export function isValidTimezone(tz: string | null | undefined): boolean {
  if (!tz || typeof tz !== 'string') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

/** Browser/runtime zone when it can be determined and is a real IANA name. */
export function detectTimezone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimezone(tz) ? tz : null;
  } catch {
    return null;
  }
}

/**
 * Resolution order: stored preference -> runtime zone -> platform default.
 * Institute timezone is intentionally absent - a learner can belong to several
 * institutes, so it cannot define "their" day.
 */
export function resolveTimezone(stored?: string | null): string {
  if (isValidTimezone(stored)) return stored as string;
  return detectTimezone() ?? PLATFORM_DEFAULT_TIMEZONE;
}

function safeZone(tz?: string | null): string {
  return isValidTimezone(tz) ? (tz as string) : PLATFORM_DEFAULT_TIMEZONE;
}

function toDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value);
}

export type ZonedParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
};

/** Civil (wall-clock) date/time of an instant in the given zone. */
export function zonedParts(value: Date | string | number, tz?: string | null): ZonedParts {
  const date = toDate(value);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: safeZone(tz),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const raw = parts.find((p) => p.type === type)?.value ?? '0';
    return Number.parseInt(raw, 10);
  };

  // hour12: false can yield 24 for midnight in some engines
  const hour = get('hour') % 24;

  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour,
    minute: get('minute'),
  };
}

/** `YYYY-MM-DD` calendar day of an instant in the given zone. */
export function dayKeyInZone(value: Date | string | number, tz?: string | null): string {
  const { year, month, day } = zonedParts(value, tz);
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Today's `YYYY-MM-DD` in the given zone. */
export function todayKeyInZone(tz?: string | null, now: Date = new Date()): string {
  return dayKeyInZone(now, tz);
}

/** Shift a `YYYY-MM-DD` key by whole days without touching any zone. */
export function addDaysToDayKey(key: string, days: number): string {
  const [y, m, d] = key.split('-').map((part) => Number.parseInt(part, 10));
  // UTC math on a civil date is safe: no zone is involved, only calendar arithmetic.
  const utc = new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1));
  utc.setUTCDate(utc.getUTCDate() + days);
  return `${utc.getUTCFullYear()}-${String(utc.getUTCMonth() + 1).padStart(2, '0')}-${String(
    utc.getUTCDate()
  ).padStart(2, '0')}`;
}

export function compareDayKeys(a: string, b: string): number {
  return a === b ? 0 : a < b ? -1 : 1;
}

export function formatTimeInZone(
  value: Date | string | number,
  tz?: string | null,
  locale?: string
): string {
  return toDate(value).toLocaleTimeString(locale, {
    timeZone: safeZone(tz),
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatDateInZone(
  value: Date | string | number,
  tz?: string | null,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
  locale?: string
): string {
  return toDate(value).toLocaleDateString(locale, { timeZone: safeZone(tz), ...options });
}

export function formatDateTimeInZone(
  value: Date | string | number,
  tz?: string | null,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
  locale?: string
): string {
  return toDate(value).toLocaleString(locale, { timeZone: safeZone(tz), ...options });
}

function rawZoneName(zone: string, value: Date, locale?: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone: zone,
      timeZoneName: 'short',
    }).formatToParts(value);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? null;
  } catch {
    return null;
  }
}

/**
 * Short zone name for an instant, e.g. `IST`, `EDT`, `BST`, or `GMT+5:45`.
 *
 * Each locale only abbreviates the zones it cares about - `en-IN` yields `IST`
 * but `GMT-4` for New York, while `en-US` does the reverse. So probe a few and
 * prefer a real abbreviation, falling back to the (unambiguous) GMT offset.
 */
export function zoneShortLabel(
  tz?: string | null,
  value: Date | string | number = new Date()
): string {
  const zone = safeZone(tz);
  const date = toDate(value);
  const locales = ['en-IN', 'en-US', 'en-GB'];
  let fallback: string | null = null;

  for (const locale of locales) {
    const name = rawZoneName(zone, date, locale);
    if (!name) continue;
    if (/^[A-Z]{2,5}$/.test(name)) return name;
    fallback ??= name;
  }

  return fallback ?? zone;
}

/** `7:00 PM IST` - a time that is unambiguous when read out of context (email). */
export function formatTimeWithZone(
  value: Date | string | number,
  tz?: string | null,
  locale?: string
): string {
  return `${formatTimeInZone(value, tz, locale)} ${zoneShortLabel(tz, value)}`;
}

export function formatDateTimeWithZone(
  value: Date | string | number,
  tz?: string | null,
  options?: Intl.DateTimeFormatOptions,
  locale?: string
): string {
  return `${formatDateTimeInZone(value, tz, options, locale)} ${zoneShortLabel(tz, value)}`;
}

/** True when two zones show a different wall clock for this instant. */
export function zonesDifferAt(
  a: string | null | undefined,
  b: string | null | undefined,
  value: Date | string | number = new Date()
): boolean {
  const left = safeZone(a);
  const right = safeZone(b);
  if (left === right) return false;
  return formatDateTimeInZone(value, left) !== formatDateTimeInZone(value, right);
}

/**
 * Sorted IANA zone list for pickers. Falls back to a small curated list on
 * engines without `Intl.supportedValuesOf`.
 */
export function listTimezones(): string[] {
  const supported = (
    Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf;
  if (typeof supported === 'function') {
    try {
      return supported('timeZone');
    } catch {
      /* fall through */
    }
  }
  return [
    'Asia/Kolkata',
    'Asia/Kathmandu',
    'Asia/Colombo',
    'Asia/Dubai',
    'Asia/Singapore',
    'Australia/Sydney',
    'Europe/London',
    'Europe/Berlin',
    'America/New_York',
    'America/Chicago',
    'America/Los_Angeles',
    'UTC',
  ];
}
